import { useCallback, useState, useEffect } from 'react';
import { X, Sparkles, TrendingUp, Users, BookOpen, Zap, Link2, MessageSquare, BarChart3, Target, Shield, Bell, Trophy, FileText, Search, AlertCircle, Settings, Play, Gift, CreditCard, Activity, Gauge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { usePageTour, pageTourConfigs } from './PageTour';

/**
 * Page Banner Component
 * 
 * Displays contextual, dismissible banners on each page with:
 * - Page purpose description
 * - Context for interpreting the page
 * - Quick tips/getting started guidance
 * 
 * Remembers dismissal via localStorage per page.
 */

// Page-specific banner configurations
export const pageBannerConfigs = {
  // Main Pages
  overview: {
    pageKey: 'overview',
    icon: BarChart3,
    badgeText: "Overview",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Your workspace at a glance",
    description: "Review recorded usage, answer feedback and knowledge coverage.",
    valueProps: [
      { icon: TrendingUp, text: "Explore activity" },
      { icon: Target, text: "Review follow-up items" },
    ],
    tip: "Open a summary card to inspect its data.",
  },
  
  // Conversations Section
  'conversations-activity': {
    pageKey: 'conversations-activity',
    icon: MessageSquare,
    badgeText: "Conversations",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Conversation activity",
    description: "Review the questions employees asked and the answers they received.",
    valueProps: [
      { icon: Zap, text: "Find a conversation" },
      { icon: TrendingUp, text: "Read the context" },
    ],
    tip: "Read follow-up questions before assessing an answer.",
  },
  'conversations-trending': {
    pageKey: 'conversations-trending',
    icon: TrendingUp,
    badgeText: "Topics",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Conversation trends",
    description: "Explore topic frequency and changes over the selected period.",
    valueProps: [
      { icon: Target, text: "Compare question counts" },
      { icon: BookOpen, text: "Review topic trends" },
    ],
    tip: "Check the underlying questions before choosing a content update.",
  },
  'conversations-feedback': {
    pageKey: 'conversations-feedback',
    icon: MessageSquare,
    badgeText: "Feedback",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Answer feedback",
    description: "Review ratings and comments alongside the related conversation.",
    valueProps: [
      { icon: TrendingUp, text: "Read comments" },
      { icon: Shield, text: "Check the answer" },
    ],
    tip: "Compare the answer with its sources before deciding what to change.",
  },
  
  // People Section
  'people-employees': {
    pageKey: 'people-employees',
    icon: Users,
    badgeText: "Employees",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Employee directory",
    description: "Review employee records, product access and account status.",
    valueProps: [
      { icon: Zap, text: "Manage records" },
      { icon: Users, text: "Review access" },
    ],
    tip: "An employee record and an activated account are separate steps.",
  },
  'people-groups': {
    pageKey: 'people-groups',
    icon: Users,
    badgeText: "Groups",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Organize employees",
    description: "Create groups and review their membership for content access and notifications.",
    valueProps: [
      { icon: Shield, text: "Review members" },
      { icon: Target, text: "Choose audiences" },
    ],
    tip: "Check membership before targeting a group.",
  },
  'people-content-team': {
    pageKey: 'people-content-team',
    icon: FileText,
    badgeText: "Team access",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Your content team",
    description: "Manage Insights membership, roles and invitations.",
    valueProps: [
      { icon: Users, text: "Review roles" },
      { icon: Shield, text: "Check invitations" },
    ],
    tip: "Use the permissions table to choose the right access.",
  },
  'people-adoption': {
    pageKey: 'people-adoption',
    icon: TrendingUp,
    badgeText: "Adoption",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Employee participation",
    description: "Review active users and participation across your organization.",
    valueProps: [
      { icon: TrendingUp, text: "Compare periods" },
      { icon: Trophy, text: "Explore departments" },
    ],
    tip: "Check with your team to understand changes in usage.",
  },
  
  // Knowledge Base Section
  'kb-content': {
    pageKey: 'kb-content',
    icon: BookOpen,
    badgeText: "Knowledge Base",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Manage your documents",
    description: "Upload, organize and review the content Eva can retrieve for answers.",
    valueProps: [
      { icon: Zap, text: "Review drafts" },
      { icon: Shield, text: "Check publication" },
    ],
    tip: "Verify processing and visibility before testing an answer.",
  },
  'kb-coverage': {
    pageKey: 'kb-coverage',
    icon: Target,
    badgeText: "Coverage",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Review your content inventory",
    description: "Explore document counts and geographic coverage.",
    valueProps: [
      { icon: Target, text: "Review scope" },
      { icon: TrendingUp, text: "Find documents" },
    ],
    tip: "Assess coverage alongside actual questions and source content.",
  },
  'kb-topics': {
    pageKey: 'kb-topics',
    icon: Search,
    badgeText: "Topics",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Organize by subject",
    description: "Use topics and subtopics to categorize documents and conversations.",
    valueProps: [
      { icon: BookOpen, text: "Manage topics" },
      { icon: Search, text: "Review subtopics" },
    ],
    tip: "Choose names your team can recognize.",
  },
  'kb-gaps': {
    pageKey: 'kb-gaps',
    icon: AlertCircle,
    badgeText: "Knowledge gaps",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Review potential gaps",
    description: "Investigate questions and feedback that may need better information or answers.",
    valueProps: [
      { icon: AlertCircle, text: "Check existing sources" },
      { icon: TrendingUp, text: "Record the resolution" },
    ],
    tip: "Verify the answer before marking a gap resolved.",
  },
  
  // Engagement Section
  'engagement-leaderboard': {
    pageKey: 'engagement-leaderboard',
    icon: Trophy,
    badgeText: "Participation",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Engagement leaderboard",
    description: "Review recorded points, levels and participation.",
    valueProps: [
      { icon: Trophy, text: "Compare activity" },
      { icon: Users, text: "Choose a period" },
    ],
    tip: "Read points in the context of the recorded activities.",
  },
  'engagement-notifications': {
    pageKey: 'engagement-notifications',
    icon: Bell,
    badgeText: "Notifications",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Messages for employees",
    description: "Create, schedule and review notifications for your chosen audience.",
    valueProps: [
      { icon: Bell, text: "Choose recipients" },
      { icon: Zap, text: "Review activity" },
    ],
    tip: "Check the message, audience and channel before sending.",
  },
  'engagement-reports': {
    pageKey: 'engagement-reports',
    icon: BarChart3,
    badgeText: "Reports",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Export workspace reports",
    description: "Choose a report type, date range and format for the data you need.",
    valueProps: [
      { icon: BarChart3, text: "Select report content" },
      { icon: TrendingUp, text: "Review schedules" },
    ],
    tip: "Check the reporting period before sharing an export.",
  },
  'engagement-referral': {
    pageKey: 'engagement-referral',
    icon: Gift,
    badgeText: "Referrals",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Your referral program",
    description: "Review the program details, your referral link and recorded rewards.",
    valueProps: [
      { icon: Gift, text: "Review program terms" },
      { icon: Users, text: "Track referrals" },
    ],
    tip: "Check the current eligibility and reward details before sharing.",
  },
  
  // Connectors
  connectors: {
    pageKey: 'connectors',
    icon: Link2,
    badgeText: "Connections",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Connect your workspace",
    description: "Configure supported connections and explore integrations in development.",
    valueProps: [
      { icon: Link2, text: "Review available tools" },
      { icon: Zap, text: "Vote for planned connections" },
    ],
    tip: "Coming Soon labels identify integrations still in development.",
  },
  
  // Settings
  settings: {
    pageKey: 'settings',
    icon: Settings,
    badgeText: "Settings",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Organization preferences",
    description: "Review your organization’s details, branding and enabled experiences.",
    valueProps: [
      { icon: Settings, text: "Review branding" },
      { icon: Shield, text: "Manage preferences" },
    ],
    tip: "Check both light and dark themes after changing your branding.",
  },
  'settings-billing': {
    pageKey: 'settings-billing',
    icon: CreditCard,
    badgeText: "Billing",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Plan and billing",
    description: "Review your subscription, seats, payment details and billing history.",
    valueProps: [
      { icon: Settings, text: "Review your plan" },
      { icon: BarChart3, text: "Check payment history" },
    ],
    tip: "Review the quote and effective date before confirming changes.",
  },
  'settings-usage': {
    pageKey: 'settings-usage',
    icon: Gauge,
    badgeText: "AI credits",
    badgeColor: 'bg-muted text-muted-foreground',
    title: "Monitor usage",
    description: "Review recorded AI credit usage and the remaining allowance.",
    valueProps: [
      { icon: Activity, text: "Compare usage" },
      { icon: Shield, text: "Check capacity" },
    ],
    tip: "Check remaining credits before a large upload or test session.",
  },
};

export function PageBanner({
  pageKey,
  config,
  className,
}) {
  const bannerConfig = config || pageBannerConfigs[pageKey];
  const { startTour } = usePageTour();
  
  const storageKey = `eva_page_banner_${bannerConfig?.pageKey || pageKey}`;
  const getInitialVisibility = useCallback(() => {
    if (!bannerConfig || typeof window === 'undefined') return false;
    return !localStorage.getItem(storageKey);
  }, [bannerConfig, storageKey]);
  const [isVisible, setIsVisible] = useState(getInitialVisibility);
  const [isDismissing, setIsDismissing] = useState(false);
  
  // Check if this page has a tour configured
  const hasTour = bannerConfig?.pageKey && pageTourConfigs[bannerConfig.pageKey]?.steps?.length > 0;
  
  useEffect(() => {
    setIsVisible(getInitialVisibility());
    setIsDismissing(false);
  }, [getInitialVisibility]);
  
  const handleDismiss = () => {
    setIsDismissing(true);
    setTimeout(() => {
      localStorage.setItem(storageKey, 'true');
      setIsVisible(false);
      setIsDismissing(false);
    }, 300);
  };
  
  const handleTakeTour = () => {
    if (bannerConfig?.pageKey) {
      startTour(bannerConfig.pageKey);
    }
  };
  
  if (!bannerConfig || !isVisible) return null;
  
  const IconComponent = bannerConfig.icon;
  
  return (
    <div
      className={cn(
        'relative bg-card border border-border rounded-lg p-5',
        'transition-all duration-300 ease-out',
        isDismissing ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0',
        className
      )}
      data-testid={`page-banner-${bannerConfig.pageKey}`}
    >
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 flex h-11 w-11 items-center justify-center rounded-md hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors z-10"
        aria-label="Dismiss banner"
        data-testid={`dismiss-banner-${bannerConfig.pageKey}`}
      >
        <X className="w-4 h-4 text-muted-foreground" />
      </button>
      
      {/* Banner content - using grid for predictable layout */}
      <div className="grid grid-cols-1 gap-4 overflow-hidden lg:grid-cols-2 xl:grid-cols-[minmax(20rem,1.15fr)_minmax(18rem,1fr)_minmax(18rem,0.9fr)] xl:items-center xl:gap-6">
        {/* Left: Icon and Title */}
        <div className="flex min-w-0 items-start gap-4 pr-8 lg:pr-0">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <IconComponent className="w-6 h-6 text-primary" />
          </div>
          
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={cn(
                'text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
                bannerConfig.badgeColor
              )}>
                {bannerConfig.badgeText}
              </span>
            </div>
            
            <h3 className="text-lg font-semibold text-foreground mb-1 whitespace-normal">
              {bannerConfig.title}
            </h3>
            
            <p className="text-sm text-muted-foreground">
              {bannerConfig.description}
            </p>
          </div>
        </div>
        
        {/* Middle: Page capabilities */}
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row lg:gap-4 xl:border-l xl:border-r xl:border-border/50 xl:px-6">
          {bannerConfig.valueProps.map((prop, index) => {
            const PropIcon = prop.icon;
            return (
              <div key={index} className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  <PropIcon className="w-4 h-4 text-muted-foreground" />
                </div>
                <span className="text-sm font-medium text-foreground">
                  {prop.text}
                </span>
              </div>
            );
          })}
        </div>
        
        {/* Right: Tour Button + Tip */}
        <div className="flex min-w-0 flex-col items-start gap-3 sm:flex-row sm:items-center lg:col-span-2 xl:col-span-1">
          {/* Take a Tour button - shows if tour is configured */}
          {hasTour && (
            <Button
              onClick={handleTakeTour}
              variant="outline"
              size="sm"
              className="group min-h-11 whitespace-nowrap"
              data-testid={`take-tour-${bannerConfig.pageKey}`}
            >
              <Play className="w-3.5 h-3.5 mr-1.5 fill-current" />
              Take a Tour
            </Button>
          )}
          
          {/* Tip only - no CTA buttons */}
          <div className="flex min-w-0 items-start gap-2 text-sm sm:max-w-[320px]">
            <Sparkles className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Tip:</strong> {bannerConfig.tip}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to manage page banner visibility
 */
export function usePageBanner(pageKey) {
  const storageKey = `eva_page_banner_${pageKey}`;
  const [hasDismissed, setHasDismissed] = useState(() => {
    if (typeof window !== 'undefined') {
      return !!localStorage.getItem(storageKey);
    }
    return false;
  });
  
  const dismiss = () => {
    localStorage.setItem(storageKey, 'true');
    setHasDismissed(true);
  };
  
  const reset = () => {
    localStorage.removeItem(storageKey);
    setHasDismissed(false);
  };
  
  const resetAll = () => {
    Object.keys(pageBannerConfigs).forEach(key => {
      localStorage.removeItem(`eva_page_banner_${key}`);
    });
  };
  
  return { hasDismissed, dismiss, reset, resetAll };
}

export default PageBanner;
